import { useState, useMemo } from 'react';
import { Download, Search } from 'lucide-react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import MatchRow from './MatchRow';
import { rowsToCsv, downloadCsv, centsToDollarsRaw } from '../csvHelpers';
import { formatMoney } from '../../../utils/formatters';

// Generic, reusable list-of-mec_match-rows table. Used by every drill-down
// tab (Tier 1, Tier 2, Tier 3 sub-buckets, Ambiguous). The table can be
// review-actionable (Tier 2 pending) or read-only (Tier 1 confirmed).

export default function MatchTable({
  rows,
  title,
  description,
  emptyMessage = 'Nothing in this bucket for the selected period.',
  showCheckboxes = false,
  csvFilename = 'mec-matches.csv',
  onMatchResolved = null, // optional — for pending review pages
}) {
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const filtered = useMemo(() => {
    if (!searchText) return rows;
    const needle = searchText.toLowerCase();
    return rows.filter((r) =>
      (r.qbo_vendor || '').toLowerCase().includes(needle)
      || (r.matched_po_number || '').toLowerCase().includes(needle)
      || (r.qbo_memo || '').toLowerCase().includes(needle)
      || String(r.matched_ro_id || '').includes(needle)
    );
  }, [rows, searchText]);

  const totalCents = useMemo(
    () => filtered.reduce((s, r) => s + (r.qbo_amount_cents || 0), 0),
    [filtered]
  );

  const exportCsv = () => {
    const csv = rowsToCsv(filtered, [
      { key: 'qbo_vendor', label: 'QBO Vendor' },
      { key: 'qbo_date', label: 'QBO Date' },
      { key: 'qbo_amount', label: 'QBO Amount', accessor: (r) => centsToDollarsRaw(r.qbo_amount_cents) },
      { key: 'qbo_memo', label: 'QBO Memo' },
      { key: 'matched_method', label: 'Method' },
      { key: 'matched_po_number', label: 'PO #' },
      { key: 'matched_ro_id', label: 'RO #' },
      { key: 'matched_date', label: 'Tekmetric Date' },
      { key: 'matched_amount', label: 'Tekmetric Amount', accessor: (r) => centsToDollarsRaw(r.matched_amount_cents) },
      { key: 'amount_delta', label: 'Δ Amount', accessor: (r) => centsToDollarsRaw(r.amount_delta_cents) },
      { key: 'date_delta_days', label: 'Δ Days' },
      { key: 'score', label: 'Score' },
      { key: 'bucket', label: 'Bucket' },
      { key: 'review_status', label: 'Review Status' },
    ]);
    downloadCsv(csvFilename, csv);
  };

  return (
    <Card padded={false}>
      <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          {title && (
            <h3 className="text-[15px] font-semibold text-[var(--color-warm-900)] leading-tight">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-[12px] text-[var(--color-warm-500)] mt-0.5">
              {description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-warm-400)] pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search vendor, PO, memo…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="h-8 pl-7 pr-3 text-[12px] bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] focus:outline-none focus:border-[var(--color-warm-400)] w-56"
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Download size={12} />}
            onClick={exportCsv}
            disabled={filtered.length === 0}
          >
            CSV
          </Button>
        </div>
      </div>

      <div className="px-5 pb-2 text-[11px] text-[var(--color-warm-500)] flex items-center gap-2">
        {filtered.length} row{filtered.length === 1 ? '' : 's'}
        <span className="text-[var(--color-warm-300)]">·</span>
        <span className="tabular-nums font-semibold text-[var(--color-warm-700)]">
          {formatMoney(totalCents / 100)} total
        </span>
        {searchText && filtered.length !== rows.length && (
          <>
            <span className="text-[var(--color-warm-300)]">·</span>
            <span>filtered from {rows.length}</span>
          </>
        )}
      </div>

      <div className="overflow-x-auto border-t border-[var(--color-warm-100)]">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[var(--color-cream-100)] border-b border-[var(--color-warm-100)]">
              {showCheckboxes && <Th width="40">&nbsp;</Th>}
              <Th>QBO Vendor / Date</Th>
              <Th align="right">Amount</Th>
              <Th>Matched To</Th>
              <Th align="right">Δ $</Th>
              <Th align="right">Δ Days</Th>
              <Th align="right">Score</Th>
              <Th>Method</Th>
              <Th align="right">Status / Action</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-5 py-10 text-center text-[12px] text-[var(--color-warm-500)]">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {filtered.map((m) => (
              <MatchRow
                key={m.id}
                match={m}
                showCheckbox={showCheckboxes}
                checked={selectedIds.includes(m.id)}
                onCheck={(id, checked) => {
                  setSelectedIds((prev) =>
                    checked ? [...prev, id] : prev.filter((x) => x !== id)
                  );
                }}
                onResolved={onMatchResolved}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Th({ children, align = 'left', width }) {
  return (
    <th
      style={width ? { width: `${width}px` } : undefined}
      className={`
        px-3 py-2.5 text-[10px] font-bold uppercase tracking-[1px]
        text-[var(--color-warm-500)]
        ${align === 'right' ? 'text-right' : 'text-left'}
      `}
    >
      {children}
    </th>
  );
}
